/**
 * Admin Portal Route Tree
 */

import {
  createRootRoute,
  createRoute,
  Outlet,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { ErrorFallback, ToastProvider, Toaster } from "@jourfx/ui";
import { AdminLayout } from "./components/AdminLayout";
import { Dashboard } from "./routes/Dashboard";
import { OrganizationsList } from "./routes/organizations/OrganizationsList";
import { OrganizationDetail } from "./routes/organizations/OrganizationDetail";
import { OrganizationCreate } from "./routes/organizations/OrganizationCreate";
import { LicensesList } from "./routes/licenses/LicensesList";
import { LicenseDetail } from "./routes/licenses/LicenseDetail";
import { TemplatesList } from "./routes/templates/TemplatesList";
import { AppointmentsList } from "./routes/appointments/AppointmentsList";
import { AppointmentCreate } from "./routes/appointments/AppointmentCreate";
import { AppointmentDetail } from "./routes/appointments/AppointmentDetail";
import { UsersList } from "./routes/users/UsersList";
import { UserDetail } from "./routes/users/UserDetail";
import { UserCreate } from "./routes/users/UserCreate";
import { UserImport } from "./routes/users/UserImport";
import { VersicherteList } from "./routes/versicherte/VersicherteList";
import { VersicherteDetail } from "./routes/versicherte/VersicherteDetail";
import { Settings } from "./routes/Settings";
import { Account } from "./routes/Account";
import { AuditLogs } from "./routes/AuditLogs";
import { System } from "./routes/System";
import { Branding } from "./routes/Branding";
import { VideoSettings } from "./routes/VideoSettings";
import { AdminsList } from "./routes/admins/AdminsList";
import { AdminCreate } from "./routes/admins/AdminCreate";
import { AdminDetail } from "./routes/admins/AdminDetail";
import { Feedback } from "./routes/Feedback";
import { EmailTemplates } from "./routes/EmailTemplates";
import { Login } from "./routes/Login";
import { Invite } from "./routes/Invite";

// Root route with layout
const rootRoute = createRootRoute({
  component: () => (
    <ToastProvider>
      <main id="main-content">
        <Outlet />
      </main>
      <Toaster />
    </ToastProvider>
  ),
  errorComponent: ({ error }: ErrorComponentProps) => (
    <ErrorFallback error={error instanceof Error ? error : new Error(String(error))} />
  ),
});

// Auth route (no layout)
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: Login,
});

// Invite route (no layout)
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$token",
  component: Invite,
});

// Authenticated layout route
const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "layout",
  component: AdminLayout,
});

// Dashboard
const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/",
  component: Dashboard,
});

// Organizations
const organizationsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/organizations",
  component: OrganizationsList,
});

const organizationCreateRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/organizations/new",
  component: OrganizationCreate,
});

const organizationDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/organizations/$orgId",
  component: OrganizationDetail,
});

// Licenses
const licensesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/licenses",
  component: LicensesList,
});

const licenseDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/licenses/$licenseId",
  component: LicenseDetail,
});

// Templates
const templatesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/templates",
  component: TemplatesList,
});

const appointmentsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/appointments",
  component: AppointmentsList,
});

const appointmentCreateRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/appointments/new",
  component: AppointmentCreate,
});

const appointmentDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/appointments/$appointmentId",
  component: AppointmentDetail,
});

// Users
const usersRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/users",
  component: UsersList,
});

const userCreateRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/users/new",
  component: UserCreate,
});

const userImportRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/users/import",
  component: UserImport,
});

const userDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/users/$userId",
  component: UserDetail,
});

const versicherteRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/versicherte",
  component: VersicherteList,
});

const versicherteDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/versicherte/$versicherteId",
  component: VersicherteDetail,
});

// Settings (Super-Admin only)
const settingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/settings",
  component: Settings,
});

// Branding (Super-Admin only)
const brandingRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/settings/branding",
  component: Branding,
});

// Video Settings (Super-Admin only)
const videoSettingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/settings/video",
  component: VideoSettings,
});

// Email Templates (Super-Admin only)
const emailTemplatesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/email-templates",
  component: EmailTemplates,
});

// Account (all admin users)
const accountRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/account",
  component: Account,
});

// Feedback
const feedbackRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/feedback",
  component: Feedback,
});

// Audit Logs
const auditLogsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/audit",
  component: AuditLogs,
});

// System Status
const systemRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/system",
  component: System,
});

// Admins (Super-Admin only)
const adminsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/admins",
  component: AdminsList,
});

const adminCreateRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/admins/new",
  component: AdminCreate,
});

const adminDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "/admins/$adminId",
  component: AdminDetail,
});

// Build route tree
export const routeTree = rootRoute.addChildren([
  loginRoute,
  inviteRoute,
  layoutRoute.addChildren([
    dashboardRoute,
    organizationsRoute,
    organizationCreateRoute,
    organizationDetailRoute,
    licensesRoute,
    licenseDetailRoute,
    templatesRoute,
    appointmentsRoute,
    appointmentCreateRoute,
    appointmentDetailRoute,
    usersRoute,
    userCreateRoute,
    userImportRoute,
    userDetailRoute,
    versicherteRoute,
    versicherteDetailRoute,
    brandingRoute,
    videoSettingsRoute,
    emailTemplatesRoute,
    settingsRoute,
    accountRoute,
    feedbackRoute,
    auditLogsRoute,
    systemRoute,
    adminsRoute,
    adminCreateRoute,
    adminDetailRoute,
  ]),
]);
